import {
    type Action,
    createBrowserHistory,
    type Location as HistoryLocation,
    type Path,
} from 'history';

import { Logger } from '@gitbook/util-debug';

export interface StaticHistoryContext {
    statusCode?: number;
    url?: string;
    action?: 'PUSH' | 'REPLACE';
    location?: object;
}

export interface LocationState {
    [key: string]: string;
}

export type Location = HistoryLocation & { state?: LocationState };
export type LocationPath = Pick<Location, 'pathname'> & Partial<Location>;

export type LocationTo = Partial<Path> & { state?: LocationState };

export type LocationListener = (update: { location: Location; action: Action }) => void;

/**
 * Controller for the low-level navigation's history.
 * The API is designed to mimic the `history` module and browser's history.
 * https://developer.mozilla.org/en-US/docs/Web/API/History
 */
export interface NavigationHistory {
    /**
     * Context object to mutate when rendering a sttic history for SSR.
     * See `createStaticHistory` for more details.
     */
    staticContext?: StaticHistoryContext;

    /**
     * Current location.
     */
    location: Location;

    /**
     * Push a new location to the history stack.
     * @param location New location.
     */
    push(location: LocationTo): void;

    /**
     * Replace the current location in the history stack.
     * @param location New location.
     */
    replace(location: LocationTo): void;

    /**
     * Go back in the history stack.
     * @param n Number of steps to go back.
     */
    go(n: number): void;

    /**
     * Execute multiple history actions,
     * while ensuring that all actions are executed in the set order.
     */
    transaction: (fn: () => void) => void;

    /**
     * Listen for history events.
     */
    listen(listener: LocationListener): () => void;

    /**
     * Block the history from changing.
     */
    block(
        shouldBlock: (currentLocation: Location, toLocation: LocationPath) => boolean,
        blocker: (tx: NavigationHistoryBlockTransaction) => void
    ): () => void;

    /**
     * Test if the history is blocked.
     */
    isBlocked(toLocation: LocationPath): boolean;
}

export interface NavigationHistoryBlockTransaction {
    location: Location;
    unblock: () => void;
}

const logger = Logger('history');

/**
 * Create a controller for the browser's history.
 * Compared to the createBrowserHistory function in the history package, this function
 * handles a strict order of operations.
 */
export function createHistory(): NavigationHistory {
    const browserHistory = createBrowserHistory();

    const pending: Array<() => void> = [];
    let inTransaction = false;
    let shouldBlock: undefined | ((currentLocation: Location, toLocation: LocationPath) => boolean);

    const executeLatestPending = () => {
        const latest = pending.shift();
        if (latest) {
            latest();
            return false;
        }

        return true;
    };

    const pushOperation = (fn: () => void) => {
        if (inTransaction) {
            pending.push(fn);
        } else {
            fn();
        }
    };

    return {
        get location() {
            return browserHistory.location as Location;
        },
        listen: (callback) => {
            return browserHistory.listen((update) => {
                callback({
                    location: update.location as Location,
                    action: update.action,
                });
            });
        },
        push: (to) => {
            const { state, ...location } = to;

            pushOperation(() => {
                logger.info('push', location, state);
                browserHistory.push(location, state);
            });
        },
        replace: (to) => {
            const { state, ...location } = to;

            pushOperation(() => {
                logger.info('replace', location, state);
                browserHistory.replace(location, state);
            });
        },
        go: (n) => {
            pushOperation(() => {
                logger.info('go', n);
                browserHistory.go(n);
            });
        },
        transaction: (fn) => {
            if (inTransaction) {
                fn();
            } else {
                const unlisten = browserHistory.listen(() => {
                    // Everytime an operation has finished, we can execute a new one.
                    if (executeLatestPending()) {
                        unlisten();
                    }
                });

                inTransaction = true;
                fn();
                inTransaction = false;

                if (executeLatestPending()) {
                    unlisten();
                }
            }
        },
        block: (shouldBlockFn, callbackFn) => {
            if (shouldBlock) {
                throw new Error('History is already blocked');
            }

            shouldBlock = shouldBlockFn;
            let stop: undefined | (() => void);

            const stopBlock = () => {
                logger.info('stop blocking history');
                if (stop) {
                    stop();
                    stop = undefined;
                }
            };

            const startBlock = () => {
                logger.info('start blocking history');
                stop = browserHistory.block((tx) => {
                    const unblock = () => {
                        logger.info('unblock history transaction');
                        // Unblock the navigation, then retry the navigation to make it happen
                        stopBlock();
                        tx.retry();
                    };

                    const blocked = shouldBlockFn(
                        browserHistory.location as Location,
                        tx.location as LocationPath
                    );

                    logger.info(`history blocked=${blocked}`);
                    if (!blocked) {
                        unblock();
                        return;
                    }

                    callbackFn({
                        location: tx.location as Location,
                        unblock,
                    });
                });
            };

            startBlock();
            return () => {
                stopBlock();
                shouldBlock = undefined;
            };
        },
        isBlocked: (toLocation) => {
            if (!shouldBlock) {
                return false;
            }

            return shouldBlock(browserHistory.location as Location, toLocation);
        },
    };
}
